import React, { useMemo, useState, useEffect } from 'react';
import { format, differenceInDays, isAfter, addDays, startOfDay } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  FiLayout,
  FiUsers,
  FiAlertTriangle,
  FiBarChart2,
  FiShield,
  FiMenu,
  FiChevronLeft,
  FiMapPin
} from 'react-icons/fi';
import arunachalLogo from '../../assets/Arunachal-Pradesh.png';
import { generateMockData, districts, purposes, ovdTypes } from './types';
import './ILPAdminStyles.css';
import './ILPAdminOverrides.css';

const ALL_DATA = generateMockData();

const APPLICANT_STATUS_OPTIONS = ['Active', 'Expiring', 'Defaulted', 'Pending', 'Upcoming'];

function maskString(value, type) {
  if (!value) return value;
  if (type === 'name') {
    return value
      .split(' ')
      .map((part) => `${part[0] || ''}${'*'.repeat(Math.max(part.length - 1, 0))}`)
      .join(' ');
  }
  if (type === 'phone') {
    return `${value.substring(0, 4)}******${value.substring(value.length - 4)}`;
  }
  if (type === 'id') {
    return `********${value.substring(value.length - 4)}`;
  }
  return value;
}

function toDateLabel(value) {
  return format(value, 'MMM dd, yyyy');
}

/** Crore-style headline for large totals (1 Cr = 1e7 INR) */
function formatRevenueCr(rupees) {
  const n = Number(rupees) || 0;
  if (n >= 1e7) {
    const cr = n / 1e7;
    const t = Number.isInteger(cr) ? String(cr) : cr >= 10 ? cr.toFixed(1) : cr.toFixed(2);
    return `Rs ${parseFloat(t)} Cr`;
  }
  return `Rs ${Math.round(n).toLocaleString('en-IN')}`;
}

function formatRupeeIn(rupees) {
  return `Rs ${Math.round(Number(rupees) || 0).toLocaleString('en-IN')}`;
}

function parseAppNumericId(id) {
  const m = String(id).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/** SOS / Overstayed / As is - demo tiers for the alerts grid */
function getAlertTier(row, now) {
  if (row.status === 'Expiring') {
    return {
      key: 'as_is',
      label: 'As is',
      hint: 'Exit window closing - routine monitoring'
    };
  }
  if (row.status === 'Defaulted') {
    const daysPast = Math.max(0, differenceInDays(now, row.exitDate));
    const bucket = parseAppNumericId(row.id) % 3;
    if (bucket === 0) {
      return {
        key: 'sos',
        label: 'SOS',
        hint: daysPast > 0 ? `Critical · ${daysPast}d past permit end` : 'Critical · escalation queue'
      };
    }
    if (bucket === 1) {
      return {
        key: 'overstayed',
        label: 'Overstayed',
        hint: `Permit ended ${toDateLabel(row.exitDate)} · no recorded exit`
      };
    }
    return {
      key: 'as_is',
      label: 'As is',
      hint: 'Standard follow-up - no distress signal'
    };
  }
  return { key: 'as_is', label: 'As is', hint: '' };
}

export default function ILPAdmin() {
  const [showSplash, setShowSplash] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: 'U-1',
    name: 'Admin User',
    role: 'Super Admin',
    district: ''
  });
  const [unmaskedIds, setUnmaskedIds] = useState(new Set());
  const [otpModal, setOtpModal] = useState({ isOpen: false, applicantId: null });
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [applicantFilters, setApplicantFilters] = useState({
    status: '',
    district: '',
    purpose: '',
    ovdType: ''
  });

  useEffect(() => {
    if (!showSplash) return undefined;
    const t = setTimeout(() => setShowSplash(false), 2500);
    return () => clearTimeout(t);
  }, [showSplash]);

  const visibleData = useMemo(() => {
    if (currentUser.role === 'District Admin' && currentUser.district) {
      return ALL_DATA.filter((row) => row.district === currentUser.district);
    }
    return ALL_DATA;
  }, [currentUser.role, currentUser.district]);

  const stats = useMemo(() => {
    const now = new Date();
    const totalRevenue = visibleData.reduce((sum, row) => sum + row.feePaid, 0);
    const footfall7d = visibleData.filter((row) => isAfter(row.entryDate, now) && differenceInDays(row.entryDate, now) <= 7).length;
    const footfall30d = visibleData.filter((row) => isAfter(row.entryDate, now) && differenceInDays(row.entryDate, now) <= 30).length;
    const footfall90d = visibleData.filter((row) => isAfter(row.entryDate, now) && differenceInDays(row.entryDate, now) <= 90).length;

    return {
      total: visibleData.length,
      active: visibleData.filter((row) => row.status === 'Active').length,
      expiring: visibleData.filter((row) => row.status === 'Expiring').length,
      defaulted: visibleData.filter((row) => row.status === 'Defaulted').length,
      pending: visibleData.filter((row) => row.status === 'Pending').length,
      upcoming: visibleData.filter((row) => row.status === 'Upcoming').length,
      inState: visibleData.filter((row) => ['Active', 'Expiring', 'Defaulted'].includes(row.status)).length,
      totalRevenue,
      footfall7d,
      footfall30d,
      footfall90d
    };
  }, [visibleData]);

  const filteredApplicants = useMemo(() => {
    let rows = visibleData;
    if (applicantFilters.status) {
      rows = rows.filter((row) => row.status === applicantFilters.status);
    }
    if (applicantFilters.district) {
      rows = rows.filter((row) => row.district === applicantFilters.district);
    }
    if (applicantFilters.purpose) {
      rows = rows.filter((row) => row.purpose === applicantFilters.purpose);
    }
    if (applicantFilters.ovdType) {
      rows = rows.filter((row) => row.ovdType === applicantFilters.ovdType);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.permitId.toLowerCase().includes(q) ||
        row.district.toLowerCase().includes(q) ||
        row.status.toLowerCase().includes(q) ||
        row.purpose.toLowerCase().includes(q) ||
        row.ovdType.toLowerCase().includes(q)
    );
  }, [searchQuery, visibleData, applicantFilters]);

  const alertFeed = useMemo(() => {
    const now = new Date();
    const q = searchQuery.trim().toLowerCase();
    let rows = visibleData.filter((r) => r.status === 'Defaulted' || r.status === 'Expiring');
    if (q) {
      rows = rows.filter(
        (row) =>
          row.name.toLowerCase().includes(q) ||
          row.permitId.toLowerCase().includes(q) ||
          row.district.toLowerCase().includes(q)
      );
    }
    const tierOrder = { sos: 0, overstayed: 1, as_is: 2 };
    return rows
      .map((row) => ({ row, tier: getAlertTier(row, now) }))
      .sort((a, b) => tierOrder[a.tier.key] - tierOrder[b.tier.key])
      .slice(0, 18);
  }, [visibleData, searchQuery]);

  const districtSummary = useMemo(() => {
    const counters = {};
    const revenue = {};
    districts.forEach((district) => {
      counters[district] = 0;
      revenue[district] = 0;
    });
    visibleData.forEach((row) => {
      counters[row.district] = (counters[row.district] || 0) + 1;
      revenue[row.district] = (revenue[row.district] || 0) + row.feePaid;
    });
    return districts
      .map((district) => ({
        district,
        count: counters[district] || 0,
        revenue: revenue[district] || 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [visibleData]);

  const footfallChartData = useMemo(() => {
    const today = startOfDay(new Date());
    const dailyCounts = [];
    for (let i = 0; i < 90; i += 1) {
      const day = startOfDay(addDays(today, i));
      const c = visibleData.filter((row) => {
        if (row.status !== 'Upcoming' && row.status !== 'Pending') return false;
        return startOfDay(row.entryDate).getTime() === day.getTime();
      }).length;
      dailyCounts.push(c);
    }

    const rollingSums = (windowSize) =>
      dailyCounts.map((_, index) => {
        const start = Math.max(0, index - windowSize + 1);
        let sum = 0;
        for (let j = start; j <= index; j += 1) sum += dailyCounts[j];
        return sum;
      });

    const raw7 = rollingSums(7);
    const raw30 = rollingSums(30);
    const raw90 = rollingSums(90);

    const f7 = Math.max(stats.footfall7d, 1);
    const f30 = Math.max(stats.footfall30d, 1);
    const f90 = Math.max(stats.footfall90d, 1);

    return dailyCounts.map((_, i) => {
      const t = i / 89;
      const ripple =
        0.38 * Math.sin(t * Math.PI * 4.2 + 0.6) +
        0.22 * Math.sin(i / 4.8 + 0.35) +
        0.14 * Math.sin(i / 2.4 + 1.1);
      const w7 = 1 + 0.28 * Math.sin(t * Math.PI * 3.1 + 1.0) + 0.14 * Math.sin(i / 3.6);
      const w30 = 1 + 0.22 * Math.sin(t * Math.PI * 2.15 + 1.9) + 0.11 * Math.sin(i / 6.2);
      const w90 = 1 + 0.16 * Math.sin(t * Math.PI * 1.55 + 2.4) + 0.09 * Math.sin(i / 11);

      const synth7 = (f7 / 7) * (3.5 + 5.5 * w7 + ripple);
      const synth30 = (f30 / 30) * (9 + 12 * w30 + ripple * 1.15);
      const synth90 = (f90 / 90) * (14 + 17 * w90 + ripple * 1.25);

      const footfall7 = Math.round(Math.max(0, 0.5 * raw7[i] + 0.5 * synth7));
      const footfall30 = Math.round(Math.max(0, 0.45 * raw30[i] + 0.55 * synth30));
      const footfall90 = Math.round(Math.max(0, 0.4 * raw90[i] + 0.6 * synth90));

      return {
        date: format(addDays(today, i), 'MMM dd'),
        footfall7,
        footfall30,
        footfall90
      };
    });
  }, [visibleData, stats.footfall7d, stats.footfall30d, stats.footfall90d]);

  const statusPieData = useMemo(() => ([
    { label: 'Active', value: stats.active, color: '#10b981' },
    { label: 'Expiring', value: stats.expiring, color: '#f59e0b' },
    { label: 'Defaulted', value: stats.defaulted, color: '#ef4444' },
    { label: 'Upcoming', value: stats.upcoming, color: '#3b82f6' }
  ]), [stats.active, stats.expiring, stats.defaulted, stats.upcoming]);

  const topExpiring = useMemo(
    () => visibleData.filter((row) => row.status === 'Expiring').slice(0, 6),
    [visibleData]
  );

  const openOtp = (applicantId) => {
    setOtpValue('');
    setOtpError('');
    setOtpModal({ isOpen: true, applicantId });
  };

  const closeOtp = () => {
    setOtpModal({ isOpen: false, applicantId: null });
    setOtpValue('');
    setOtpError('');
  };

  const verifyOtp = () => {
    if (otpValue === '1234' && otpModal.applicantId) {
      setUnmaskedIds((prev) => new Set([...prev, otpModal.applicantId]));
      closeOtp();
      return;
    }
    setOtpError('Invalid OTP. Use 1234 for demo.');
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', Icon: FiLayout },
    { id: 'applicants', label: 'Applicants', Icon: FiUsers },
    { id: 'alerts', label: 'Alerts', Icon: FiAlertTriangle },
    { id: 'revenue', label: 'Revenue', Icon: FiBarChart2 },
    { id: 'rbac', label: 'Access Control', Icon: FiShield }
  ];

  const revenueChartData = useMemo(
    () => [...districtSummary].sort((a, b) => b.revenue - a.revenue),
    [districtSummary]
  );

  const revenueBarColors = ['#059669', '#10b981', '#34d399', '#6ee7b7', '#14b8a6', '#0d9488', '#0f766e'];
  const systemUsers = [
    { name: 'Admin User', email: 'admin@eilp.gov.in', role: 'Super Admin', scope: 'All Districts', status: 'Active' },
    { name: 'Officer Singh', email: 'singh@eilp.gov.in', role: 'Border Officer', scope: 'Checkposts', status: 'Active' },
    { name: 'District Official', email: 'official.tawang@eilp.gov.in', role: 'District Admin', scope: 'Tawang', status: 'Active' },
    { name: 'Jane Doe', email: 'jane.doe@eilp.gov.in', role: 'Border Officer', scope: 'Checkposts', status: 'Inactive' }
  ];

  if (showSplash) {
    return (
      <div className="ilp-admin-splash" role="dialog" aria-modal="true" aria-labelledby="ilp-admin-splash-title">
        <div className="ilp-admin-splash-glow" aria-hidden />
        <div className="ilp-admin-splash-content">
          <div className="ilp-admin-splash-logo ilp-admin-splash-animate-entrance">
            <img src={arunachalLogo} alt="Government of Arunachal Pradesh" />
          </div>
          <div className="ilp-admin-splash-text ilp-admin-splash-animate-fade-up">
            <h1 id="ilp-admin-splash-title">eILP Admin</h1>
            <div className="ilp-admin-splash-divider">
              <span />
              <p>Admin Portal</p>
              <span />
            </div>
          </div>
          <div className="ilp-admin-splash-loader" aria-hidden>
            <div className="ilp-admin-splash-loader-bar" />
          </div>
          <button type="button" className="ilp-admin-splash-continue" onClick={() => setShowSplash(false)}>
            Continue
          </button>
        </div>
        <div className="ilp-admin-splash-footer ilp-admin-splash-animate-fade">
          <p>Department of IT & Communication</p>
          <p>Government of Arunachal Pradesh</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ilp-admin-page ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="ilp-admin-sidebar">
        <div className="ilp-admin-sidebar-top">
          {!sidebarCollapsed && <h2>eILP Admin</h2>}
          <button
            type="button"
            className="ilp-admin-collapse-btn"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {sidebarCollapsed ? <FiMenu size={18} /> : <FiChevronLeft size={18} />}
          </button>
        </div>

        <nav className="ilp-admin-nav">
          {navItems.map((item) => {
            const Icon = item.Icon;
            return (
              <button
                key={item.id}
                type="button"
                className={activeTab === item.id ? 'active' : ''}
                onClick={() => setActiveTab(item.id)}
                title={sidebarCollapsed ? item.label : ''}
              >
                <span className="ilp-admin-nav-icon" aria-hidden>
                  <Icon size={18} />
                </span>
                {!sidebarCollapsed && <span className="ilp-admin-nav-label">{item.label}</span>}
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="ilp-admin-main">
        <header className="ilp-admin-header">
          <div>
            <h1>ILP Administration</h1>
            <p>{currentUser.role}{currentUser.district ? ` - ${currentUser.district}` : ''}</p>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, permit, district, status, purpose, OVD"
          />
        </header>

        {activeTab === 'dashboard' && (
          <>
            <div className="ilp-admin-stats">
              <article><h3>Total Applicants</h3><strong>{stats.total}</strong></article>
              <article><h3>In State</h3><strong>{stats.inState}</strong></article>
              <article><h3>Exiting (48h)</h3><strong>{stats.expiring}</strong></article>
              <article><h3>Footfall (30d)</h3><strong>{stats.footfall30d}</strong></article>
              <article><h3>Defaulted</h3><strong>{stats.defaulted}</strong></article>
              <article><h3>Revenue</h3><strong>{formatRevenueCr(stats.totalRevenue)}</strong></article>
            </div>

            <div className="ilp-admin-grid ilp-admin-grid-dashboard">
              <div className="ilp-admin-card">
                <h3>Expected Tourist Footfall (30 days)</h3>
                <div className="ilp-admin-mini-stats">
                  <span>7d: <strong>{stats.footfall7d}</strong></span>
                  <span>30d: <strong>{stats.footfall30d}</strong></span>
                  <span>90d: <strong>{stats.footfall90d}</strong></span>
                </div>
                <div className="ilp-admin-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={footfallChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickCount={9}
                        allowDecimals={false}
                        domain={[0, (dataMax) => Math.max(10, Math.ceil(dataMax * 1.2))]}
                      />
                      <Tooltip />
                      <Area type="natural" dataKey="footfall90" stroke="#1d4ed8" fill="#93c5fd" fillOpacity={0.35} />
                      <Area type="natural" dataKey="footfall30" stroke="#0ea5e9" fill="#67e8f9" fillOpacity={0.35} />
                      <Area type="natural" dataKey="footfall7" stroke="#10b981" fill="#86efac" fillOpacity={0.35} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="ilp-admin-legend">
                  <span><em style={{ background: '#10b981' }} />7 Day Layer</span>
                  <span><em style={{ background: '#0ea5e9' }} />30 Day Layer</span>
                  <span><em style={{ background: '#1d4ed8' }} />90 Day Layer</span>
                </div>
              </div>

              <div className="ilp-admin-card">
                <h3>Permit Status Distribution</h3>
                <div className="ilp-admin-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusPieData} dataKey="value" nameKey="label" innerRadius={55} outerRadius={86}>
                        {statusPieData.map((entry) => (
                          <Cell key={entry.label} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="ilp-admin-legend">
                  {statusPieData.map((entry) => (
                    <span key={entry.label}>
                      <em style={{ background: entry.color }} />
                      {entry.label} ({entry.value})
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="ilp-admin-grid">
              <div className="ilp-admin-card">
                <h3>District Overview</h3>
                <table>
                  <thead>
                    <tr><th>District</th><th>Applicants</th><th>Revenue</th></tr>
                  </thead>
                  <tbody>
                    {districtSummary.slice(0, 10).map((item) => (
                      <tr key={item.district}>
                        <td>{item.district}</td>
                        <td>{item.count}</td>
                        <td>{formatRupeeIn(item.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ilp-admin-card">
                <h3>Critical Monitoring (Expiring)</h3>
                <table>
                  <thead>
                    <tr><th>Applicant</th><th>Permit</th><th>Exit Date</th></tr>
                  </thead>
                  <tbody>
                    {topExpiring.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {unmaskedIds.has(row.id) ? row.name : maskString(row.name, 'name')}
                          {!unmaskedIds.has(row.id) && (
                            <button type="button" className="ilp-admin-link-btn" onClick={() => openOtp(row.id)}>
                              Unlock
                            </button>
                          )}
                        </td>
                        <td>{row.permitId}</td>
                        <td>{toDateLabel(row.exitDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'applicants' && (
          <div className="ilp-admin-card">
            <div className="ilp-admin-applicants-head">
              <h3>Applicants</h3>
              <p className="ilp-admin-muted ilp-admin-applicants-count">
                Showing <strong>{filteredApplicants.length}</strong> of {visibleData.length}
              </p>
            </div>
            <div className="ilp-admin-applicants-toolbar">
              <label htmlFor="ilp-filter-status">
                Status
                <select
                  id="ilp-filter-status"
                  value={applicantFilters.status}
                  onChange={(e) => setApplicantFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="">All statuses</option>
                  {APPLICANT_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label htmlFor="ilp-filter-district">
                District
                <select
                  id="ilp-filter-district"
                  value={applicantFilters.district}
                  onChange={(e) => setApplicantFilters((f) => ({ ...f, district: e.target.value }))}
                >
                  <option value="">All districts</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label htmlFor="ilp-filter-purpose">
                Purpose
                <select
                  id="ilp-filter-purpose"
                  value={applicantFilters.purpose}
                  onChange={(e) => setApplicantFilters((f) => ({ ...f, purpose: e.target.value }))}
                >
                  <option value="">All purposes</option>
                  {purposes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </label>
              <label htmlFor="ilp-filter-ovd">
                OVD
                <select
                  id="ilp-filter-ovd"
                  value={applicantFilters.ovdType}
                  onChange={(e) => setApplicantFilters((f) => ({ ...f, ovdType: e.target.value }))}
                >
                  <option value="">All OVD types</option>
                  {ovdTypes.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="ilp-admin-applicants-clear"
                onClick={() => setApplicantFilters({ status: '', district: '', purpose: '', ovdType: '' })}
              >
                Clear filters
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>OVD Type</th>
                  <th>ID Number</th>
                  <th>Permit ID</th>
                  <th>District</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplicants.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ilp-admin-muted ilp-admin-applicants-empty">
                      No applicants match the current filters or search.
                    </td>
                  </tr>
                ) : null}
                {filteredApplicants.map((row) => {
                  const unlocked = unmaskedIds.has(row.id);
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="ilp-admin-person">
                          <div className="ilp-admin-avatar">{row.name.charAt(0)}</div>
                          <div>
                            <div className="ilp-admin-person-name-row">
                              <span>{unlocked ? row.name : maskString(row.name, 'name')}</span>
                              {!unlocked && (
                                <button type="button" className="ilp-admin-link-btn" onClick={() => openOtp(row.id)}>
                                  Unlock
                                </button>
                              )}
                            </div>
                            <div className="ilp-admin-muted">{unlocked ? row.phone : maskString(row.phone, 'phone')}</div>
                          </div>
                        </div>
                      </td>
                      <td>{row.ovdType}</td>
                      <td>
                        {unlocked ? row.idNumber : maskString(row.idNumber, 'id')}
                        {!unlocked && (
                          <button type="button" className="ilp-admin-link-btn" onClick={() => openOtp(row.id)}>
                            Unlock
                          </button>
                        )}
                      </td>
                      <td>{row.permitId}</td>
                      <td>{row.district}</td>
                      <td>{toDateLabel(row.entryDate)}</td>
                      <td>{toDateLabel(row.exitDate)}</td>
                      <td><span className={`ilp-admin-status ${row.status.toLowerCase()}`}>{row.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="ilp-admin-alerts-wrap">
            {alertFeed.length === 0 ? (
              <p className="ilp-admin-muted ilp-admin-alerts-empty">No alerts match the current view or search.</p>
            ) : (
              <div className="ilp-admin-grid ilp-admin-alert-grid">
                {alertFeed.map(({ row, tier }) => (
                  <article
                    className={`ilp-admin-alert-card ilp-admin-alert-card--${tier.key}`}
                    key={row.id}
                  >
                    <div className="ilp-admin-alert-card-top">
                      <span className={`ilp-admin-alert-badge ilp-admin-alert-badge--${tier.key}`}>{tier.label}</span>
                      <span className={`ilp-admin-status ${row.status.toLowerCase()}`}>{row.status}</span>
                    </div>
                    <h4>{row.name}</h4>
                    <p>
                      {row.permitId} - {row.district}
                    </p>
                    {tier.hint ? <p className="ilp-admin-alert-hint">{tier.hint}</p> : null}
                    <p className="ilp-admin-alert-exit">Permit exit: {toDateLabel(row.exitDate)}</p>
                    <div className="ilp-admin-alert-actions">
                      <button type="button">Flag</button>
                      <button type="button" className="secondary">Call</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="ilp-admin-revenue">
            <div className="ilp-admin-revenue-summary">
              <article className="ilp-admin-revenue-stat">
                <h4>Total revenue</h4>
                <strong>{formatRevenueCr(stats.totalRevenue)}</strong>
                <p>Across {stats.total} applicants in view</p>
              </article>
              <article className="ilp-admin-revenue-stat ilp-admin-revenue-stat-alt">
                <h4>Avg. fee per applicant</h4>
                <strong>
                  {stats.total > 0 ? formatRupeeIn(stats.totalRevenue / stats.total) : 'Rs 0'}
                </strong>
                <p>Based on visible dataset</p>
              </article>
            </div>
            <div className="ilp-admin-card ilp-admin-revenue-chart-card">
              <div className="ilp-admin-revenue-chart-head">
                <div>
                  <h3>Revenue by district</h3>
                  <p className="ilp-admin-revenue-sub">Horizontal bars - highest revenue at the top</p>
                </div>
              </div>
              <div className="ilp-admin-chart ilp-admin-chart-revenue">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={revenueChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 28, left: 4, bottom: 8 }}
                    barCategoryGap={10}
                  >
                    <defs>
                      <linearGradient id="ilpRevenueBar0" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#047857" />
                        <stop offset="100%" stopColor="#34d399" />
                      </linearGradient>
                      <linearGradient id="ilpRevenueBar1" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0d9488" />
                        <stop offset="100%" stopColor="#5eead4" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="#e2e8f0" horizontal vertical={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: '#64748b', fontSize: 11 }}
                      axisLine={{ stroke: '#cbd5e1' }}
                      tickLine={false}
                      tickFormatter={(value) => {
                        if (value >= 1e7) return `${(value / 1e7).toFixed(1)} Cr`;
                        if (value >= 1e5) return `${(value / 1e5).toFixed(1)} L`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                        return String(value);
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="district"
                      width={118}
                      tick={{ fill: '#334155', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(241, 245, 249, 0.85)' }}
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 25px rgba(15, 23, 42, 0.12)'
                      }}
                      formatter={(value) => [formatRupeeIn(value), 'Revenue']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="revenue" radius={[0, 8, 8, 0]} maxBarSize={22} stroke="#ecfdf5" strokeWidth={1}>
                      {revenueChartData.map((entry, index) => (
                        <Cell
                          key={entry.district}
                          fill={index % 2 === 0 ? 'url(#ilpRevenueBar0)' : 'url(#ilpRevenueBar1)'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rbac' && (
          <div className="ilp-admin-rbac-page">
            <header className="ilp-admin-rbac-hero">
              <div className="ilp-admin-rbac-hero-icon" aria-hidden>
                <FiShield size={28} />
              </div>
              <div>
                <h2>Role-based access control</h2>
                <p>Switch session role for this demo. District admins only see data for the selected district.</p>
              </div>
            </header>

            <div className="ilp-admin-rbac-cards">
              <button
                type="button"
                className={`ilp-admin-rbac-card ${currentUser.role === 'Super Admin' ? 'selected' : ''}`}
                onClick={() => setCurrentUser({ id: 'U-1', name: 'Admin User', role: 'Super Admin', district: '' })}
              >
                <span className="ilp-admin-rbac-card-icon"><FiShield size={22} /></span>
                <h4>Super Admin</h4>
                <p>Full access to all districts, revenue, and settings.</p>
              </button>
              <button
                type="button"
                className={`ilp-admin-rbac-card ${currentUser.role === 'Border Officer' ? 'selected' : ''}`}
                onClick={() => setCurrentUser({ id: 'U-2', name: 'Officer Singh', role: 'Border Officer', district: '' })}
              >
                <span className="ilp-admin-rbac-card-icon"><FiUsers size={22} /></span>
                <h4>Border Officer</h4>
                <p>Verification and entry or exit logging at checkposts.</p>
              </button>
              <div
                className={`ilp-admin-rbac-card ilp-admin-rbac-card-static ${currentUser.role === 'District Admin' ? 'selected' : ''}`}
              >
                <span className="ilp-admin-rbac-card-icon"><FiMapPin size={22} /></span>
                <h4>District Admin</h4>
                <p>Restricted to one district. Pick the district below.</p>
                <label className="ilp-admin-rbac-district-label" htmlFor="ilp-rbac-district">District</label>
                <select
                  id="ilp-rbac-district"
                  className="ilp-admin-rbac-select"
                  value={currentUser.role === 'District Admin' ? currentUser.district : districts[0]}
                  onChange={(e) => {
                    setCurrentUser({
                      id: 'U-3',
                      name: 'District Official',
                      role: 'District Admin',
                      district: e.target.value
                    });
                  }}
                >
                  {districts.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ilp-admin-card ilp-admin-system-users-card">
              <div className="ilp-admin-system-users-head">
                <h3>System users</h3>
                <p className="ilp-admin-system-users-desc">Demo directory - names shown for admin accounts only.</p>
              </div>
              <div className="ilp-admin-table-wrap">
                <table className="ilp-admin-table-users">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>District scope</th>
                      <th>Status</th>
                      <th className="ilp-admin-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemUsers.map((user) => (
                      <tr key={`${user.email}-${user.role}`}>
                        <td>
                          <div className="ilp-admin-person">
                            <div className="ilp-admin-avatar ilp-admin-avatar-lg">{user.name.charAt(0)}</div>
                            <div>
                              <div className="ilp-admin-user-name">{user.name}</div>
                              <div className="ilp-admin-muted">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className="ilp-admin-role-pill">{user.role}</span></td>
                        <td>{user.scope}</td>
                        <td>
                          <span className={`ilp-admin-status ${user.status.toLowerCase()}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="ilp-admin-td-actions">
                          <button type="button" className="ilp-admin-btn-edit">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>

      {otpModal.isOpen && (
        <div className="ilp-admin-modal-backdrop" onClick={closeOtp}>
          <div className="ilp-admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Security Verification</h3>
            <p>Enter OTP to unmask personal data (demo: 1234).</p>
            <input
              type="text"
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              placeholder="Enter OTP"
              maxLength={4}
            />
            {otpError ? <p className="ilp-admin-error">{otpError}</p> : null}
            <div className="ilp-admin-modal-actions">
              <button type="button" className="secondary" onClick={closeOtp}>Cancel</button>
              <button type="button" onClick={verifyOtp}>Verify</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
